/* import __COLOCATED_TEMPLATE__ from './manage-connection.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';

interface Args {
  isOpen: boolean;
  onClose: () => void;
}

export default class StandaloneSalesforceManageConnection extends Component<Args> {
  @service declare appService: any;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare notificationsService: $TSFixMe;
  @service declare redirectService: any;
  @service declare intl: IntlService;

  @tracked selectedDomain = this.salesforceConfig.isSandbox ? 'sandbox' : 'production';

  @action startConnectFlow(selectedDomain = 'production') {
    let uri = this.oauthUrls.connect;
    let params = `app_id_code=${this.appService.app.id}&sandbox=${selectedDomain === 'sandbox'}&is_conversation_syncing_enabled=${this.finStandaloneService.isConversationSyncingEnabled}`;
    this.redirectService.redirect(`${uri}?${params}`);
  }

  @action async disconnectApp() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t(
        'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-message',
        {
          environmentLabel: this.salesforceConfig.environmentLabel,
        },
      ),
      confirmButtonText: this.intl.t(
        'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-button-label',
      ),
      primaryButtonType: 'primary-destructive',
    });

    try {
      if (isConfirmed) {
        await this.salesforceConfig.disconnectApp();
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-success-message',
          ),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t(
          'standalone.salesforce.setup.manage-connection.sync-case-history-confirmation-error-message',
        ),
      });
    }
  }

  @action onDomainChange(domain: string) {
    this.selectedDomain = domain;
  }

  get oauthUrls() {
    let { protocol, host } = window.location;
    return {
      connect: `${protocol}//${host}/standalone/salesforce/connect`,
      connectCallback: `${protocol}//${host}/standalone/salesforce/connect_callback`,
    };
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get isConnected() {
    return this.salesforceConfig.isConnected;
  }

  get connectedBannerText() {
    return this.salesforceConfig.isSandbox
      ? this.intl.t('standalone.salesforce.setup.manage-connection.connected-banner-sandbox-text')
      : this.intl.t(
          'standalone.salesforce.setup.manage-connection.connected-banner-production-text',
        );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ManageConnection': typeof StandaloneSalesforceManageConnection;
    'standalone/salesforce/setup/manage-connection': typeof StandaloneSalesforceManageConnection;
  }
}
